import axios, { type AxiosResponse } from 'axios';
import { isMobileUA, STORAGE_KEY } from '@/utils/platform';
import { reportError } from '@/utils/errorReport';
import router from '@/router';

const baseURL = import.meta.env.VITE_API_BASE || '/api';

interface ApiRes<T = unknown> {
  code: number;
  msg: string;
  data: T;
}

const http = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 15000,
});

function extractData<T>(res: AxiosResponse<ApiRes<T>>): T {
  return res.data.data;
}

http.interceptors.request.use((config) => {
  return config;
});

http.interceptors.response.use(
  (res) => {
    const { code, msg } = res.data;
    if (code !== 0) {
      const errMsg = msg || '操作失败';
      console.error(`[HTTP] 业务错误 ${res.config.method?.toUpperCase()} ${res.config.url} code=${code} msg=${errMsg}`, res.data);
      reportError({
        level: 'error',
        type: 'api_biz_error',
        message: errMsg,
        url: res.config.url,
        method: res.config.method?.toUpperCase(),
        status: res.status,
        extra: { code, msg },
      });
      if (isMobileUA()) {
        import('vant').then(({ showToast }) => showToast(errMsg)).catch(() => {});
      } else {
        import('element-plus').then(({ ElMessage }) => ElMessage.error(errMsg)).catch(() => {});
      }
      return Promise.reject(new Error(errMsg));
    }
    return res;
  },
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || '';
    const method = err.config?.method?.toUpperCase() || 'UNKNOWN';
    const backendMsg = err.response?.data?.msg;
    const errMsg = backendMsg || err.message || '网络错误';
    const isNetworkError = !err.response;
    const detailMsg = isNetworkError
      ? `网络错误: ${err.message} (无响应, 可能是网络断开/CORS/超时) ${method} ${url}`
      : `HTTP ${status} ${method} ${url} - ${errMsg}`;

    if (status === 401) {
      const isMeRequest = url.includes('/auth/me');
      console.warn(`[HTTP] 401 未授权 ${method} ${url}`);
      if (!isMeRequest && !url.includes('/auth/admin-login/') && !url.includes('/auth/login')) {
        localStorage.removeItem(STORAGE_KEY);
        const currentPath = window.location.pathname;
        const isLoginPage = currentPath === '/pc/login' || currentPath === '/m/login';
        if (!isLoginPage) {
          if (isMobileUA()) {
            router.replace({ path: '/m/login', query: { redirect: currentPath } });
          } else {
            router.replace({ path: '/pc/login', query: { redirect: currentPath } });
          }
        }
      }
      return Promise.reject(err);
    }

    console.error(`[HTTP] ${detailMsg}`, err.response?.data || err, err.stack ? { stack: err.stack } : '');
    reportError({
      level: 'error',
      type: status ? 'api_http_error' : 'network_error',
      message: detailMsg,
      url,
      method,
      status,
      extra: err.response?.data || { errMessage: err.message },
    });

    if (isMobileUA()) {
      import('vant').then(({ showToast }) => showToast(errMsg)).catch(() => {});
    } else {
      import('element-plus').then(({ ElMessage }) => ElMessage.error(errMsg)).catch(() => {});
    }
    return Promise.reject(err);
  },
);

export function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  return http.get<ApiRes<T>>(url, { params }).then(extractData);
}

export function post<T>(url: string, data?: unknown): Promise<T> {
  return http.post<ApiRes<T>>(url, data).then(extractData);
}

export function put<T>(url: string, data?: unknown): Promise<T> {
  return http.put<ApiRes<T>>(url, data).then(extractData);
}

export function del<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  return http.delete<ApiRes<T>>(url, { params }).then(extractData);
}

export function patch<T>(url: string, data?: unknown): Promise<T> {
  return http.patch<ApiRes<T>>(url, data).then(extractData);
}

export function page<T>(url: string, params?: Record<string, unknown>): Promise<{ list: T[]; total: number }> {
  return http.get<ApiRes<{ list: T[]; total: number }>>(url, { params }).then(extractData);
}

export default http;
